import { Card, CardContent, Button, Select, MenuItem, FormControl, InputLabel, TextField, InputAdornment, Autocomplete } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';

export type FilterField = {
  name: string
  label: string
  type?: 'text' | 'number' | 'date' | 'select'
  placeholder?: string
  options?: Array<{ label: string; value: string }>
}

type Props = {
    searchLabel?: string;
    search?: string;
    handleReset: () => void;
    setSearchKeyword?: (e: string) => void;
    filters?: FilterField[];
    filterValues?: Record<string, any>;
    onFilterChange?: (filterName: string, value: any) => void;
}

const BordFilterCard = ({ 
  searchLabel, 
  search, 
  handleReset, 
  setSearchKeyword,
  filters,
  filterValues = {},
  onFilterChange
}: Props) => {
  // Use provided filters or default to single search field
  const baseFields = filters || (searchLabel ? [{
    name: 'search',
    label: searchLabel,
    type: 'text',
    placeholder: 'Search...'
  }] : [])

  return (
    <Card
      sx={{
        height: '100%',
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
        borderRadius: "12px",
        padding: 1,
        mb: 3,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: "0px 6px 25px rgba(0, 0, 0, 0.08)",
        },
      }}
    >
      <CardContent>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-12 items-center">
          {baseFields.map((field) => {
            // Render Select/Autocomplete Input
            if (field.type === 'select') {
              const currentValue = field.options?.find(opt => opt.value === filterValues[field.name]) || null;
              
              return (
                <div key={field.name} className="col-span-1 md:col-span-4">
                  <Autocomplete
                    size="small"
                    options={field.options || []}
                    getOptionLabel={(option) => option.label}
                    value={currentValue}
                    onChange={(event, newValue) => {
                      if (onFilterChange) {
                        onFilterChange(field.name, newValue ? newValue.value : '')
                      }
                    }}
                    renderInput={(params) => (
                      <TextField 
                        {...params} 
                        label={field.label} 
                        placeholder={field.placeholder || "Select..."}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                          }
                        }}
                      />
                    )}
                  />
                </div>
              )
            }

            // Render Text/Number Input
            return (
              <div key={field.name} className="col-span-1 md:col-span-4">
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  type={field.type === 'number' ? 'number' : 'text'}
                  value={field.name === 'search' ? search : filterValues[field.name] || ''}
                  onChange={(e) => {
                    if (field.name === 'search' && setSearchKeyword) {
                      setSearchKeyword(e.target.value)
                    } else if (onFilterChange) {
                      onFilterChange(field.name, field.type === 'number' ? Number(e.target.value) : e.target.value)
                    }
                  }}
                  placeholder={field.placeholder || "Search..."}
                  variant="outlined"
                  InputProps={{
                    sx: { borderRadius: '8px' },
                    startAdornment: field.name === 'search' ? (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                      </InputAdornment>
                    ) : null
                  }}
                />
              </div>
            )
          })}

          <div className="col-span-1 md:col-span-4 flex justify-center md:justify-start">
            <Button
              variant="outlined"
              size="medium"
              onClick={handleReset}
              sx={{
                borderRadius: "8px",
                width: '113px',
                textTransform: 'none',
                borderColor: '#e0e0e0',
                color: '#666',
                '&:hover': {
                  borderColor: '#bdbdbd',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

export default BordFilterCard